// src/BlogPost.js
import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { firestore, doc, getDoc } from './firebase-config';
import { AuthContext } from './AuthContext';
import DOMPurify from 'dompurify';
import './BlogPost.css';

const BlogPost = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const { blogDarkMode, toggleBlogDarkMode } = useContext(AuthContext);

  const handlePrint = (withoutImages = false) => {
    const page = document.querySelector('.blog-page');
    if (withoutImages && page) {
      page.classList.add('print-no-images');
      const cleanup = () => {
        page.classList.remove('print-no-images');
        window.removeEventListener('afterprint', cleanup);
      };
      window.addEventListener('afterprint', cleanup);
    }
    window.print();
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate();
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  useEffect(() => {
    const fetchPost = async () => {
      const postDoc = await getDoc(doc(firestore, 'savedclay_posts', id));
      setPost({ id: postDoc.id, ...postDoc.data() });
    };
    fetchPost();
    window.scrollTo({ top: 0, behavior: 'instant' });
    document.getElementById('lower-page')?.scrollIntoView({ behavior: 'instant' });
  }, [id]);

  if (!post) {
    return <div className="blog-loading">Loading...</div>;
  }

  return (
    <div className={`blog-page ${blogDarkMode ? 'blog-dark' : 'blog-light'}`}>
      <div className="blog-toolbar">
        <button
          className="blog-print-btn"
          onClick={() => handlePrint(false)}
          aria-label="Print or save as PDF"
        >
          ⎙ Print / PDF
        </button>
        <button
          className="blog-print-btn"
          onClick={() => handlePrint(true)}
          aria-label="Print without images"
        >
          ⎙ Print (no images)
        </button>
        <button
          className="blog-dark-toggle"
          onClick={toggleBlogDarkMode}
          aria-label="Toggle dark mode"
        >
          {blogDarkMode ? '☀ Light' : '☾ Dark'}
        </button>
      </div>
      <article className="blog-post">
        {post.coverImage && (
          <img
            src={post.coverImage}
            alt={post.title}
            className="blog-post-cover"
          />
        )}
        <div className="blog-post-meta">
          <h1>{post.title}</h1>
          <p className="blog-post-date">
            Published: {formatDate(post.createdAt)}
            {post.authorName && <> | By: {post.authorName}</>}
          </p>
        </div>
        <div
          className="post-content"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(
            (post.content || '').replace(/\[(\d+)\]/g, '<sup class="footnote-ref"><a href="#fn-$1" id="fnref-$1">[$1]</a></sup>'),
            { ADD_TAGS: ['img', 'sup'], ADD_ATTR: ['src', 'alt', 'id', 'href'] }
          ) }}
        />

        {/* Footnotes section */}
        {post.footnotes && post.footnotes.length > 0 && (
          <section className="post-footnotes">
            <h3>Footnotes</h3>
            <ol>
              {post.footnotes.map((note, i) => (
                <li key={i} id={`fn-${i + 1}`}>
                  {note} <a href={`#fnref-${i + 1}`} className="footnote-back">↩</a>
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* YouTube embed */}
        {post.youtubeUrl && (() => {
          const match = post.youtubeUrl.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/))([^&?/\s]{11})/);
          const videoId = match ? match[1] : null;
          return videoId ? (
            <div className="post-youtube">
              <iframe
                src={`https://www.youtube.com/embed/${videoId}`}
                title="YouTube video"
                allowFullScreen
              />
            </div>
          ) : null;
        })()}

        {/* Gallery */}
        {post.galleryImages && post.galleryImages.length > 0 && (
          <div className={`post-gallery post-gallery--${Math.min(post.galleryImages.length, 3)}`}>
            {post.galleryImages.map((img, i) => (
              <figure key={i} className="post-gallery-item">
                <img src={img.url} alt={img.caption || `Image ${i + 1}`} />
                {img.caption && <figcaption>{img.caption}</figcaption>}
              </figure>
            ))}
          </div>
        )}

        {/* Print-only watermark */}
        <div className="print-watermark">SavedClay.com</div>
      </article>
    </div>
  );
};

export default BlogPost;
